<!--
  =====================================================================
  ACTIVITY CATEGORY CARD - CATEGORY WITH IMAGE
  =====================================================================
  
  Activity category card with optional image display.
  
  FEATURES:
  - Image-on-left, text-on-right layout
  - Image fallback with invisible placeholder
  - Color scheme support
  - Click navigation to category details
  - Animated vertical bar on hover
  - Responsive resizing
  
  BEHAVIOR:
  - Image fixed width for layout consistency
  - Placeholder maintains space when no image
  - Text remains readable at all sizes
  
  =====================================================================
-->

<template>
  <article :class="['category-card', `category-card--${colorScheme}`]">
    <a :href="link" @click.prevent="navigateTo(link)">
      
      <div class="vertical-bar"></div>

      <div class="card-inner">
        
        <div v-if="firstImage" class="card-image">
          <div class="image-container">
            <img :src="firstImage" :alt="card.label" />
          </div>
        </div>

        <div v-else class="card-image-placeholder"></div>

        <div class="card-content">
          <h3 class="card-title">{{ card.title }}</h3>
          <p class="card-text">{{ card.eyebrow }}</p>
        </div>
      </div>
    </a>
  </article>
</template>

<script setup>
import { computed } from 'vue'

// ───────────────────────────────────────────────────────────────────
// PROPS DEFINITION
// ───────────────────────────────────────────────────────────────────

/**
 * Component props definition
 * @prop {Object} card - Card data with title, eyebrow, image, slug
 * @prop {String} colorScheme - Color theme (default: 'green')
 * @prop {String} macroArea - Activity macro area slug
 * @prop {String} category - Activity category slug
 */
const props = defineProps({
  card: {
    type: Object,
    required: true
  },
  colorScheme: {
    type: String,
    default: 'green'
  },
  macroArea: {
    type: String,
    required: true
  },
  category: {
    type: String,
    required: true
  }
})

// ───────────────────────────────────────────────────────────────────
// COMPUTED PROPERTIES
// ───────────────────────────────────────────────────────────────────

/**
 * Generates card detail link
 * Format: /fondazione/attivita/{macroArea}/{category}/{slug}
 */
const link = computed(() => `/fondazione/attivita/${props.macroArea}/${props.category}/${props.card.slug}`)

/**
 * Extracts first image from card
 * Handles both string and array formats
 */
const firstImage = computed(() => {
  const img = props.card?.image
  if (Array.isArray(img)) {
    return img.length > 0 ? img[0] : null
  }
  return img || null
})

// ───────────────────────────────────────────────────────────────────
// NAVIGATION HANDLER
// ───────────────────────────────────────────────────────────────────

/**
 * Navigates to category details page
 * @param {String} url - Category URL
 */
function navigateTo(url) {
  window.location.href = url
}
</script>

<style scoped lang="scss">
@import "@/assets/styles/partials/variables";
@import "@/assets/styles/partials/compat";

.category-card {
  position: relative;
  display: block;
  background: $surface-base;
  color: $text-color;
  box-shadow: 0 2px 4px rgba($clr-brand-dark, 0.05);
  border: 1px solid $clr-border;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  overflow: hidden;
  height: 180px;

  a {
    text-decoration: none;
    color: inherit;
    display: block;
    position: relative;
    height: 100%;
  }

  @media (hover: hover) {
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 16px rgba($clr-brand-dark, 0.12);
      border-color: rgba($clr-brand-dark, 0.2);
    }
  }

  /* Animated vertical bar */
  .vertical-bar {
    position: absolute;
    left: 0;
    top: 0;
    width: 3px;
    height: 100%;
    pointer-events: none;
    z-index: 2;

    &::before,
    &::after {
      content: "";
      position: absolute;
      left: 0;
      width: 3px;
      background: $clr-border;
      height: 0;
      transition: height 0.5s ease, background 0.5s ease;
    }

    &::before { bottom: 50%; }
    &::after  { top: 50%; }
  }

  @media (hover: hover) {
    &:hover .vertical-bar::before,
    &:hover .vertical-bar::after {
      height: 50%;
      background: $clr-brand-dark;
    }

    &:hover .card-image img {
      transform: scale(1.05);
    }
  }

  /* Card inner layout */
  .card-inner {
    display: flex;
    align-items: center;
    height: 100%;
  }

  /* Image container */
  .card-image,
  .card-image-placeholder {
    flex: 0 0 260px;
    height: 100%;
    position: relative;
  }
  
  .card-image {
    overflow: hidden;
    background: $clr-bg-light;

    .image-container {
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        transition: transform 0.45s ease;
      }
    }
  }

  .card-image-placeholder {
    background: $clr-bg-light;
  }

  /* Text content */
  .card-content {
    flex: 1;
    padding: $space-2 $space-2-5;
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-width: 0;
  }

  .card-title {
    font-size: clamp(1.4rem, 2.5vw, 1.8rem);
    margin: 0 0 $space-0-75;
    font-weight: 600;
    line-height: 1.2;
    font-family: 'Karla', sans-serif;
    color: $text-color;
    overflow: hidden;
    text-overflow: ellipsis;
    @include line-clamp(2, 1.2, $overflow: false);
  }

  .card-text {
    font-size: clamp(0.9rem, 1.4vw, 1rem);
    color: $clr-text-light;
    margin: 0;
    line-height: 1.4;
    overflow: hidden;
    text-overflow: ellipsis;
    @include line-clamp(2, 1.4, $overflow: false);
  }
}

/* ------------------------------------------------------------------
   Responsive breakpoints
------------------------------------------------------------------ */
@media (max-width: 1300px) {
  .category-card {
    height: 160px;
    .card-image,
    .card-image-placeholder { flex: 0 0 220px; }
    .card-content { padding: $space-1-5 $space-2; }
    .card-title { font-size: clamp(1.2rem, 2.2vw, 1.6rem); margin-bottom: $space-0-5; }
    .card-text { font-size: clamp(0.85rem, 1.2vw, 0.95rem); }
  }
}

@media (max-width: 920px) {
  .category-card {
    height: 150px;
    .card-image,
    .card-image-placeholder { flex: 0 0 200px; }
    .card-content { padding: $space-1 $space-1-5; }
    .card-title { font-size: clamp(1.1rem, 2vw, 1.5rem); }
    .card-text { font-size: clamp(0.8rem, 1.1vw, 0.9rem); }
  }
}

@media (max-width: 480px) {
  .category-card {
    height: auto;
    .card-inner { 
      flex-direction: column; 
      align-items: stretch;
    }
    .card-image,
    .card-image-placeholder { flex: 0 0 auto; width: 100%; height: 150px; }
    .card-content { 
      padding: $space-1-5; 
      width: 100%;
    }
  }
}
</style>